/**
 * Client side of
 * `GET /api/waadoo-platform/projects/<p>/namespaces/<ns>/init-report`
 * (plugins/odoo-backend/src/waadooPlatform/initReportRoute.ts): the
 * report of the last run of the Odoo chart's init job in one environment —
 * first initialisation or update, which modules it installed and updated,
 * the state of the project's custom modules, and the end of the log of a
 * failed run. The « Modules » card of the branch page's overview
 * (`InitReportCard.tsx`), read for it by `useInitReport.ts`.
 *
 * RUNNING (chart 0.9.7). The job also writes a report at the START of its
 * run, `status: 'running'`, with the plan (`plannedInstall`,
 * `plannedUpdate`) and its pod's name, and replaces it at the end. One that
 * stays running past {@link INIT_REPORT_STALE_MS} is a pod that died before
 * its final write: {@link initReportPhase} reads it as `interrupted`.
 *
 * `InitReport` is re-declared rather than imported, for the reason given in
 * `pipelines.ts`: it is the contract of the route, to be changed on both
 * sides at once (`InitReport` in the backend's `initReport.ts`, which
 * validates and caps it first).
 *
 * NEVER IN THE WAY. A report missing a core field (status, mode, the start,
 * and for a finished run the end and the duration) reads as none, and the
 * card is then not shown; a list entry of the wrong type is dropped alone,
 * an optional string of the wrong type reads as `''`, a pod name that is not
 * one as none.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import {
  type StatusChipStatus,
  shortImageName,
} from '@internal/plugin-waadoo-core';

export interface InitReportModule {
  name: string;
  /** `''` when the job could not read one. */
  version: string;
  /** Odoo's `ir_module_module.state`, as the job wrote it. */
  state: string;
}

/** What every report holds, running or finished. */
interface InitReportBase {
  mode: 'init' | 'update';
  /** ISO instant. */
  startedAt: string;
  /** The full reference; `''` when the job wrote none. */
  image: string;
  /** `[]` when the job wrote none — a running report may not. */
  customModules: InitReportModule[];
  /** `''` when the job wrote none. */
  customHash: string;
  /** The job's pod (chart 0.9.7); absent when not given or not a pod name. */
  podName?: string;
  /** What the run was to install and update (chart 0.9.7); `[]` if absent. */
  plannedInstall: string[];
  plannedUpdate: string[];
}

export interface FinishedInitReport extends InitReportBase {
  status: 'succeeded' | 'failed';
  /** ISO instant. */
  finishedAt: string;
  durationSeconds: number;
  installed: string[];
  updated: string[];
  /** Failed runs only. */
  logTail?: string;
}

/** A run that has written its start and not (yet) its end. */
export interface RunningInitReport extends InitReportBase {
  status: 'running';
}

export type InitReport = FinishedInitReport | RunningInitReport;

/**
 * Past this since its start, a running report is read as interrupted: the
 * init Job's `activeDeadlineSeconds` is 1800 (30 min, the Odoo chart's
 * `job-init.yaml`) — Kubernetes kills its pod then, without the final write
 * — plus 5 min for the clocks of the job's node and of the reader's browser
 * to disagree and for the report to reach the backend's cache.
 */
export const INIT_REPORT_STALE_MS = 35 * 60_000;

/**
 * What the card says of a report: its status, and for a running one,
 * whether it still can be (`running`) or has outlived its job
 * (`interrupted`).
 */
export type InitReportPhase =
  | 'succeeded'
  | 'failed'
  | 'running'
  | 'interrupted';

export function initReportPhase(
  report: InitReport,
  now: number,
): InitReportPhase {
  if (report.status !== 'running') {
    return report.status;
  }
  return now - Date.parse(report.startedAt) > INIT_REPORT_STALE_MS
    ? 'interrupted'
    : 'running';
}

/**
 * The words and the chip colour of a custom module's state. Odoo's states
 * (`ir_module_module.state`): `installed`; `to install`, `to upgrade` and
 * `to remove`, an operation scheduled and not done — which after a run
 * means the run did not get to it; `uninstalled` and `uninstallable`. Plus
 * `absent`, not Odoo's but listed in the chart's contract for this card,
 * read as a module missing from the database — as is an empty state. A
 * state none of these, under its own name in grey.
 */
export type ModuleStateKey =
  | 'installed'
  | 'toInstall'
  | 'toUpgrade'
  | 'toRemove'
  | 'absent'
  | 'uninstallable';

export const MODULE_STATE_LABEL_KEYS = {
  installed: 'initReport.state.installed',
  toInstall: 'initReport.state.toInstall',
  toUpgrade: 'initReport.state.toUpgrade',
  toRemove: 'initReport.state.toRemove',
  absent: 'initReport.state.absent',
  uninstallable: 'initReport.state.uninstallable',
} as const satisfies Record<ModuleStateKey, string>;

const STATE_KEYS: Record<string, ModuleStateKey> = {
  installed: 'installed',
  'to install': 'toInstall',
  'to upgrade': 'toUpgrade',
  'to remove': 'toRemove',
  uninstalled: 'absent',
  absent: 'absent',
  '': 'absent',
  uninstallable: 'uninstallable',
};

/** A state's key, undefined for a state not listed above. */
export function moduleStateKey(state: string): ModuleStateKey | undefined {
  return Object.prototype.hasOwnProperty.call(STATE_KEYS, state)
    ? STATE_KEYS[state]
    : undefined;
}

/** Green when installed, amber when pending, red when missing. */
export function moduleStateStatus(
  key: ModuleStateKey | undefined,
): StatusChipStatus {
  switch (key) {
    case 'installed':
      return 'healthy';
    case 'toInstall':
    case 'toUpgrade':
    case 'toRemove':
      return 'degraded';
    case 'absent':
    case 'uninstallable':
      return 'failed';
    default:
      return 'unknown';
  }
}

/**
 * The tag of an image reference, what the card's pill shows — `4f2c9e1` for
 * `registry.example.org/waadoo/app1:4f2c9e1`; for a reference without a tag,
 * its short name (`shortImageName`, images.ts: the last path segment, or a
 * short digest).
 */
export function imageTag(reference: string): string {
  const short = shortImageName(reference);
  const colon = short.indexOf(':');
  return colon !== -1 && !short.includes('@') ? short.slice(colon + 1) : short;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isInstant = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

const stringOr = (value: unknown) => (typeof value === 'string' ? value : '');

/**
 * A pod's name: a DNS-1123 subdomain of at most 253 characters, the
 * backend's `POD_NAME_PATTERN` — it is put in a URL (`branchLogsPath`,
 * branchTabs.ts).
 */
const POD_NAME_PATTERN =
  /^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/;

const podName = (value: unknown): string | undefined =>
  typeof value === 'string' &&
  value.length <= 253 &&
  POD_NAME_PATTERN.test(value)
    ? value
    : undefined;

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is string => typeof item === 'string' && item !== '',
      )
    : [];

/** The payload's report; null when it holds none or a broken one. */
export function readInitReport(payload: unknown): InitReport | null {
  if (!isRecord(payload) || !isRecord(payload.report)) {
    return null;
  }
  const report = payload.report;
  if (
    (report.status !== 'succeeded' &&
      report.status !== 'failed' &&
      report.status !== 'running') ||
    (report.mode !== 'init' && report.mode !== 'update') ||
    !isInstant(report.startedAt)
  ) {
    return null;
  }
  const customModules = Array.isArray(report.customModules)
    ? report.customModules.flatMap((item): InitReportModule[] =>
        isRecord(item) && typeof item.name === 'string' && item.name !== ''
          ? [
              {
                name: item.name,
                version: stringOr(item.version),
                state: stringOr(item.state),
              },
            ]
          : [],
      )
    : [];
  const pod = podName(report.podName);
  const base: InitReportBase = {
    mode: report.mode,
    startedAt: report.startedAt,
    image: stringOr(report.image),
    customModules,
    customHash: stringOr(report.customHash),
    ...(pod ? { podName: pod } : {}),
    plannedInstall: stringList(report.plannedInstall),
    plannedUpdate: stringList(report.plannedUpdate),
  };
  // The backend keeps no end field on a running report; nor does this one.
  if (report.status === 'running') {
    return { ...base, status: 'running' };
  }
  if (
    !isInstant(report.finishedAt) ||
    typeof report.durationSeconds !== 'number' ||
    !Number.isFinite(report.durationSeconds) ||
    report.durationSeconds < 0
  ) {
    return null;
  }
  return {
    ...base,
    status: report.status,
    finishedAt: report.finishedAt,
    durationSeconds: report.durationSeconds,
    installed: stringList(report.installed),
    updated: stringList(report.updated),
    // The backend keeps it for a failed run only; so does this reader.
    ...(report.status === 'failed' &&
    typeof report.logTail === 'string' &&
    report.logTail !== ''
      ? { logTail: report.logTail }
      : {}),
  };
}

/** One request. Rejects on a failed request. */
export async function fetchInitReport(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** The project of the page — its `waadoo.ovh/project` label. */
  project: string;
  /** The namespace the branch's ArgoCD Application deploys to. */
  namespace: string;
}): Promise<InitReport | null> {
  // Absolute backend URL: in development the frontend runs on another port.
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-platform');
  const response = await options.fetchApi.fetch(
    `${baseUrl}/projects/${encodeURIComponent(
      options.project,
    )}/namespaces/${encodeURIComponent(options.namespace)}/init-report`,
  );
  if (!response.ok) {
    throw new Error(`Init report API: HTTP ${response.status}`);
  }
  return readInitReport(await response.json());
}
